import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Globe, Menu, X } from "lucide-react";
import { useLang, LANGS } from "@/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LANG_LABEL = { tr: "Türkçe", en: "English", ru: "Русский" };

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#about", label: t("nav_about") },
    { href: "#products", label: t("nav_products") },
    { href: "#services", label: t("nav_services") },
    { href: "#partners", label: t("nav_partners") },
    { href: "#contact", label: t("nav_contact") },
  ];

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#ffffff]/80 backdrop-blur-xl border-b border-black/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 h-20 flex items-center justify-between">
        <a href="#top" data-testid="logo" className="flex items-center gap-1.5 group">
          <img src="/brand/logo.png" alt="DMD Group logo" className="h-9 w-auto" />
          <span className="font-mono text-[9px] tracking-[0.25em] text-[#d81f2a] mt-1">INT.</span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.slice(1)}`}
              className="font-mono text-xs uppercase tracking-[0.15em] text-[#5b6472] hover:text-[#0f1115] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="lang-toggle"
                className="flex items-center gap-2 border border-black/12 px-3 py-2 text-xs font-mono uppercase tracking-wider text-[#0f1115] hover:border-[#d81f2a] transition-colors"
              >
                <Globe className="w-3.5 h-3.5" />
                {lang}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#ffffff] border-black/10 rounded-none min-w-[140px]">
              {LANGS.map((l) => (
                <DropdownMenuItem
                  key={l}
                  data-testid={`lang-option-${l}`}
                  onClick={() => setLang(l)}
                  className={`font-mono text-xs uppercase tracking-wider cursor-pointer rounded-none ${
                    lang === l ? "text-[#d81f2a]" : "text-[#5b6472]"
                  } focus:bg-black/5 focus:text-[#0f1115]`}
                >
                  {LANG_LABEL[l]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <a
            href="#contact"
            data-testid="nav-cta"
            className="hidden md:inline-flex items-center bg-[#d81f2a] text-[#ffffff] px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] font-bold hover:bg-white transition-colors"
          >
            {t("hero_cta")}
          </a>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-[#0f1115]"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#ffffff] border-t border-black/10 px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="font-mono text-sm uppercase tracking-widest text-[#5b6472]"
            >
              {l.label}
            </a>
          ))}
          <Link to="/admin/login" className="font-mono text-sm uppercase tracking-widest text-[#d81f2a]">
            {t("nav_admin")}
          </Link>
        </div>
      )}
    </motion.header>
  );
}
